import { Dispatch, SetStateAction, useEffect } from "react";
import { Banner, FoodsTextInput, useFormBaseContext, WhiteBox } from "@foodpilot/foods";
import { Typography, Stack, useTheme, FormControl, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ProductFormType } from "../../ProductForm.types";
import { ProductTagField } from "./Fields/ProductTagField";
import { GammeField } from "./Fields/GammeField";
import { BrandField } from "./Fields/BrandField";
import { ProductRefField } from "./Fields/ProductRefField";
import { MODIFY, PRODUCT_SETTINGS_ORGANIZATION, useFoodpilotContext } from "src/context/FoodpilotContext";
import { JsonAttributesField } from "./Fields/JsonAttributesField";
import { OwnedByField } from "./Fields/OwnedByField";
import { AssociatedStructuresField } from "./Fields/AssociatedStructuresField";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { SsqAutocomplete, SsqOption } from "src/components/Selector/SsqSelector";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { ProductPhotoUpload } from "src/pages/Product/components/ProductPhotoUpload";
import { RadioBooleanField } from "src/components/Radio/RadioBooleanField";
import { useGetAllTagTypes } from "src/api/client-api/tag/useGetAllTagTypes";
import { type BrandCollectionState, useGetAllBrands } from "src/api/client-api/brand/useGetAllBrand";
import { useOrganizationSectionVisibility } from "src/pages/Product/hooks/useOrganizationSectionVisibility";

const productReferenceSchema = z.object({
    id: z.number(),
    name: z.string().min(1),
});

const brandSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

const productLineSchema = z.object({
    "@id": z.string(),
    name: z.string(),
});

// `productLine` is no longer required at the form level — the API accepts a null
// productLine, and per product spec the field is optional even when the active
// company has product lines. The `hasProductLines` flag still controls whether the
// `GammeField` is rendered (see below), but it no longer gates form validity.
const productSchema = z.object({
    title: z.string().min(1),
    ssq_id: z.number().optional(),
    clientId: z.string().min(1),
    productReference: productReferenceSchema.optional(),
    organic: z.boolean(),
    netWeight: z.number().gt(0),
    brand: brandSchema.optional(),
    productLine: productLineSchema.optional().nullable(),
    tags: z.record(z.string(), z.array(z.string())).optional(),
    ownedBy: z.object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
    }),
});

export type ProductFormData = z.infer<typeof productSchema>;

export const shouldShowBrandField = (status: BrandCollectionState["status"]): boolean => status === "success-value";

type ProductInformationProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    companyIds: { "@id": string; id: number; name: string };
};

export const ProductInformation = (props: ProductInformationProps) => {
    const { t } = useTranslation();
    const { lockForward, unlockForward } = useFormBaseContext();
    const { canUser, selectedCompany } = useFoodpilotContext();
    const theme = useTheme();
    const { isAdmin } = useFoodpilotContext();
    const isAdminUser = isAdmin();

    const { product, setProduct } = props;
    const { ssq: productSsq, isLoading: isSsqLoading } = useGetOneSsq(product.ssq_id ?? undefined);

    // Hide the "Tags" block when no tag type is defined for the product context.
    // Params mirror those used inside JsonTagSelector so React Query deduplicates the request.
    const { tagTypes = [] } = useGetAllTagTypes({ context: "product", itemsPerPage: 100 });
    const hasProductTagTypes = tagTypes.length > 0;

    // Hide the "Brand" field when no brand exists for the active organization.
    // We issue a stable existence-only request (`itemsPerPage: 1`, no name
    // filter) so it is independent of the search-driven query inside
    // BrandField and React Query can dedupe with the matching call below.
    const { brandCollectionState } = useGetAllBrands({ itemsPerPage: 1 });
    const hasBrands = shouldShowBrandField(brandCollectionState.status);
    const { shouldHide: shouldHideOrganizationSection, hasProductLines } = useOrganizationSectionVisibility({
        currentCompanyId: props.companyIds.id,
    });

    const {
        control,
        formState: { errors, isValid },
    } = useForm<ProductFormData>({
        resolver: zodResolver(productSchema),
        mode: "onChange",
        reValidateMode: "onBlur",
        defaultValues: {
            title: product.title ?? "",
            ssq_id: product.ssq_id ?? undefined,
            clientId: product.clientId ?? "",
            productReference: product.productReference ?? undefined,
            organic: product.organic ?? false,
            netWeight: product.netWeight ?? undefined,
            brand: product.brand ?? undefined,
            productLine: product.productLine ?? undefined,
            tags: product.tags ?? {},
            ownedBy: product.ownedBy ?? props.companyIds,
        },
    });

    const isOwner = props.companyIds.id === product.ownedBy?.id;
    const canI = canUser(MODIFY, PRODUCT_SETTINGS_ORGANIZATION);
    const canShowAssociatedStructure = isOwner && canI;

    useEffect(() => {
        if (isValid) {
            unlockForward();
        } else {
            lockForward(t("form.invalidstep"));
        }
    }, [isValid]);

    return (
        <Stack gap={theme.spacing(3)}>
            <WhiteBox>
                <Stack padding={theme.spacing(4)} gap={theme.spacing(4)}>
                    <Stack gap={theme.spacing(0.5)}>
                        <Typography variant={"h2"}>
                            {t("entity.product_identity.section_title", "Product Identity")}
                        </Typography>
                        <Typography variant={"body-regular"} sx={{ color: theme.custom.grey[2000] }}>
                            {t(
                                "entity.product_identity.section_subtitle",
                                "Define the information used to identify your product and calculate its indicators.",
                            )}
                        </Typography>
                    </Stack>

                    <ProductPhotoUpload product={product} setProduct={setProduct} />

                    <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection={"column"} gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">{`${t("product.identity.name", "Product name")}*`}</Typography>
                                <FoodsTextInput
                                    {...field}
                                    error={!!errors.title}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setProduct((prevState: ProductFormType) => ({
                                            ...prevState,
                                            title: value ?? null,
                                        }));
                                    }}
                                />
                            </Stack>
                        )}
                    />
                    <Controller
                        name="clientId"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection={"column"} gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">{`${t("product.identity.unique_identifier", "Unique identifier")}*`}</Typography>
                                <FoodsTextInput
                                    {...field}
                                    error={!!errors.clientId}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setProduct((prevState: ProductFormType) => ({
                                            ...prevState,
                                            clientId: value ?? null,
                                        }));
                                    }}
                                />
                            </Stack>
                        )}
                    />
                    <Controller
                        name="productReference"
                        control={control}
                        render={({ field }) => (
                            <ProductRefField
                                productReference={product.productReference}
                                setNewProductRef={(value) => {
                                    field.onChange(value);
                                    setProduct((prevState: ProductFormType) => ({
                                        ...prevState,
                                        productReference: value ?? null,
                                    }));
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="organic"
                        control={control}
                        render={({ field }) => (
                            <RadioBooleanField
                                title={t("product.v2.identity.organic_product", "Organic product")}
                                value={field.value ?? false}
                                onChange={(value) => {
                                    field.onChange(value);
                                    setProduct((prevState: ProductFormType) => ({
                                        ...prevState,
                                        organic: value,
                                    }));
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="netWeight"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection={"column"} gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">{`${t("product.identity.netweight", "Net weight")}*`}</Typography>
                                <FoodsTextInput
                                    {...field}
                                    value={field.value?.toString() ?? ""}
                                    error={!!errors.netWeight}
                                    onChange={(value: string) => {
                                        field.onChange(+value);
                                        setProduct((prevState: ProductFormType) => ({
                                            ...prevState,
                                            netWeight: +value,
                                        }));
                                    }}
                                    unit={t("weight.grams", "grams")}
                                    ExtraInputProps={{
                                        type: "number",
                                    }}
                                />
                                <Banner variant={"themed"}>
                                    <Typography variant="body">{t("product.netweight.info")}</Typography>
                                </Banner>
                            </Stack>
                        )}
                    />

                    {isAdminUser && (
                        <Controller
                            name="ssq_id"
                            control={control}
                            render={({ field }) => (
                                <FormControl size="small">
                                    <Typography variant="body-semibold" sx={{ paddingBottom: "0.5rem" }}>
                                        {t("product.identity.ssq", "SSQ")}
                                    </Typography>

                                    {!isSsqLoading ?
                                        <SsqAutocomplete
                                            value={productSsq ?? null}
                                            entityType="product"
                                            onChange={(ssq: SsqOption | null) => {
                                                const ssqId = ssq?.id ?? null;

                                                field.onChange(ssqId);
                                                setProduct({ ...product, ssq_id: ssqId });
                                            }}
                                        />
                                    :   <CircularProgress />}
                                </FormControl>
                            )}
                        />
                    )}
                </Stack>
            </WhiteBox>

            {!shouldHideOrganizationSection && (
                <WhiteBox>
                    <Stack padding={theme.spacing(4)} gap={theme.spacing(4)}>
                        <Stack gap={theme.spacing(0.5)}>
                            <Typography variant={"h2"}>
                                {t("entity.organization.section_title", "Organisation")}
                            </Typography>
                            <Typography variant={"body-regular"} sx={{ color: theme.custom.grey[2000] }}>
                                {t(
                                    "entity.organization.product.section_subtitle",
                                    "Define the product’s positioning within your organization and the entities that will be able to contribute to it.",
                                )}
                            </Typography>
                        </Stack>

                        {hasBrands && (
                            <Controller
                                name="brand"
                                control={control}
                                render={({ field }) => (
                                    <BrandField
                                        brandIRI={product.brand?.["@id"] || null}
                                        setBrand={(value) => {
                                            field.onChange(value);
                                            setProduct((prevState: ProductFormType) => ({
                                                ...prevState,
                                                brand: value ?? null,
                                            }));
                                        }}
                                    />
                                )}
                            />
                        )}
                        {hasProductLines && (
                            <Controller
                                name="productLine"
                                control={control}
                                render={({ field }) => (
                                    <GammeField
                                        productLineIRI={product.productLine?.["@id"] ?? null}
                                        setGamme={(value) => {
                                            field.onChange(value);
                                            setProduct((prevState: ProductFormType) => ({
                                                ...prevState,
                                                productLine: value,
                                            }));
                                        }}
                                    />
                                )}
                            />
                        )}

                        {/* Show only if I am not the owner */}
                        {isOwner === false && (
                            <Controller
                                name="ownedBy"
                                control={control}
                                render={({
                                    field,
                                    // onChange
                                }) => (
                                    <OwnedByField
                                        value={field.value ?? null}
                                        onChange={(value) => {
                                            setProduct((prevState: ProductFormType) => ({
                                                ...prevState,
                                                ownedBy: value,
                                            }));
                                        }}
                                    />
                                )}
                            />
                        )}

                        {canShowAssociatedStructure && (
                            <AssociatedStructuresField
                                ownedBy={product.ownedBy}
                                value={product.shares}
                                onChange={(value) => {
                                    setProduct((prevState: ProductFormType) => ({
                                        ...prevState,
                                        shares: value,
                                    }));
                                }}
                            />
                        )}
                    </Stack>
                </WhiteBox>
            )}

            {hasProductTagTypes && (
                <WhiteBox>
                    <Stack padding={theme.spacing(4)} gap={theme.spacing(4)}>
                        <Stack gap={theme.spacing(0.5)}>
                            <Typography variant={"h2"}>{t("entity.tags.title", "Tags")}</Typography>
                            <Typography variant={"body-regular"} sx={{ color: theme.custom.grey[2000] }}>
                                {t(
                                    "entity.product.tags.subtitle",
                                    "Add tags to facilitate the classification and analysis of your product.",
                                )}
                            </Typography>
                        </Stack>
                        <Controller
                            name="tags"
                            control={control}
                            render={({ field }) => (
                                <ProductTagField product={product} setProduct={setProduct} field={field} />
                            )}
                        />
                    </Stack>
                </WhiteBox>
            )}

            {/* JSON Attributes Field - handled separately */}
            <JsonAttributesField product={product} setProduct={setProduct} />

            {/* <Box>
                <Typography variant="body">{t("form.mandatory_field")}</Typography>
            </Box> */}
        </Stack>
    );
};
